/**
 * Gamification Manager
 * Handles automatic reward distribution for various activities
 */

import { doc, updateDoc, setDoc, increment, arrayUnion, serverTimestamp, getDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { TCOINS_TYPES } from '../context/TCoinsContext';

// Activity Types
export const ACTIVITY_TYPES = {
  CLOCK_IN: 'CLOCK_IN',
  CLOCK_OUT: 'CLOCK_OUT',
  SHIFT_COMPLETE: 'SHIFT_COMPLETE',
  STOCK_IN: 'STOCK_IN',
  STOCK_OUT: 'STOCK_OUT',
  INSPECTION: 'INSPECTION',
  BORROW_EQUIPMENT: 'BORROW_EQUIPMENT',
  RETURN_EQUIPMENT: 'RETURN_EQUIPMENT',
  REPORT_ISSUE: 'REPORT_ISSUE',
  SEND_REPAIR_REQUEST: 'SEND_REPAIR_REQUEST',
  COMPLETE_REPAIR: 'COMPLETE_REPAIR',
  JOIN_TEAM: 'JOIN_TEAM',
  DAILY_LOGIN: 'DAILY_LOGIN',
  GIVE_GIFT: 'GIVE_GIFT',
  RECEIVE_GIFT: 'RECEIVE_GIFT',
  LEVEL_UP: 'LEVEL_UP'
};

// Rewards Table
export const REWARDS = {
  [ACTIVITY_TYPES.CLOCK_IN]: {
    points: 10,
    xp: 20,
    coins: 2,
    coinType: TCOINS_TYPES.VIRTUAL
  },
  [ACTIVITY_TYPES.CLOCK_OUT]: {
    points: 40,
    xp: 80,
    coins: 8,
    coinType: TCOINS_TYPES.VIRTUAL
  },
  [ACTIVITY_TYPES.SHIFT_COMPLETE]: {
    points: 50,
    xp: 100,
    coins: 10,
    coinType: TCOINS_TYPES.VIRTUAL
  },
  [ACTIVITY_TYPES.STOCK_IN]: {
    points: 20,
    xp: 30,
    coins: 5,
    coinType: TCOINS_TYPES.VIRTUAL
  },
  [ACTIVITY_TYPES.STOCK_OUT]: {
    points: 20,
    xp: 30,
    coins: 5,
    coinType: TCOINS_TYPES.VIRTUAL
  },
  [ACTIVITY_TYPES.INSPECTION]: {
    points: 15,
    xp: 25,
    coins: 3,
    coinType: TCOINS_TYPES.VIRTUAL
  },
  [ACTIVITY_TYPES.BORROW_EQUIPMENT]: {
    points: 20,
    xp: 30,
    coins: 5,
    coinType: TCOINS_TYPES.VIRTUAL
  },
  [ACTIVITY_TYPES.RETURN_EQUIPMENT]: {
    points: 20,
    xp: 30,
    coins: 5,
    coinType: TCOINS_TYPES.VIRTUAL
  },
  [ACTIVITY_TYPES.REPORT_ISSUE]: {
    points: 30,
    xp: 50,
    coins: 10,
    coinType: TCOINS_TYPES.VIRTUAL
  },
  [ACTIVITY_TYPES.SEND_REPAIR_REQUEST]: {
    points: 25,
    xp: 40,
    coins: 5,
    coinType: TCOINS_TYPES.VIRTUAL
  },
  [ACTIVITY_TYPES.COMPLETE_REPAIR]: {
    points: 50,
    xp: 80,
    coins: 15,
    coinType: TCOINS_TYPES.VIRTUAL
  },
  [ACTIVITY_TYPES.JOIN_TEAM]: {
    points: 100,
    xp: 200,
    coins: 20,
    coinType: TCOINS_TYPES.VIRTUAL
  },
  [ACTIVITY_TYPES.DAILY_LOGIN]: {
    points: 5,
    xp: 10,
    coins: 5,
    coinType: TCOINS_TYPES.VIRTUAL
  },
  [ACTIVITY_TYPES.GIVE_GIFT]: {
    points: 0,
    xp: 0,
    coins: 0,
    coinType: TCOINS_TYPES.VIRTUAL
  },
  [ACTIVITY_TYPES.RECEIVE_GIFT]: {
    points: 0,
    xp: 0,
    coins: 0,
    coinType: TCOINS_TYPES.VIRTUAL
  },
  [ACTIVITY_TYPES.LEVEL_UP]: {
    points: 0,
    xp: 0,
    coins: 100,
    coinType: TCOINS_TYPES.REAL
  }
};

/**
 * Award rewards for an activity
 * @param {string} userId - User ID
 * @param {string} activityType - Activity type (from ACTIVITY_TYPES)
 * @param {object} metadata - Additional metadata (teamId, equipmentId, etc.)
 * @param {function} addTCoins - Function to add T Coins (from TCoinsContext)
 * @returns {Promise<object>} - Result with rewards given
 */
export const awardRewards = async (userId, activityType, metadata = {}, addTCoins = null) => {
  try {
    if (!userId || !activityType) {
      throw new Error('userId and activityType are required');
    }

    const reward = REWARDS[activityType];
    if (!reward) {
      console.warn(`No reward defined for activity type: ${activityType}`);
      return { success: false, error: 'No reward defined for this activity' };
    }

    // Skip if no rewards
    if (reward.points === 0 && reward.xp === 0 && reward.coins === 0) {
      return { success: true, rewards: { points: 0, xp: 0, coins: 0 } };
    }

    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      throw new Error('User not found');
    }

    const userData = userSnap.exists() ? userSnap.data() : {};
    const currentPoints = userData.totalPoints || 0;
    const currentXP = userData.currentXP || 0;
    const totalXP = userData.totalXP || 0;

    // Calculate new values
    const newPoints = currentPoints + reward.points;
    const newXP = currentXP + reward.xp;
    const newTotalXP = totalXP + reward.xp;

    // Update user document
    const updateData = {
      totalPoints: newPoints,
      currentXP: newXP,
      totalXP: newTotalXP,
      updatedAt: serverTimestamp()
    };

    // Initialize stats if not exists
    if (!userData.stats) {
      updateData.stats = {
        totalShifts: 0,
        totalInspections: 0,
        totalBorrows: 0,
        totalRepairs: 0,
        perfectAttendanceDays: 0,
        currentStreak: 0,
        longestStreak: 0
      };
    }

    // Update stats based on activity type
    const stats = userData.stats || {};
    switch (activityType) {
      case ACTIVITY_TYPES.SHIFT_COMPLETE:
        updateData.stats = {
          ...stats,
          totalShifts: (stats.totalShifts || 0) + 1
        };
        break;
      case ACTIVITY_TYPES.INSPECTION:
        updateData.stats = {
          ...stats,
          totalInspections: (stats.totalInspections || 0) + 1
        };
        break;
      case ACTIVITY_TYPES.BORROW_EQUIPMENT:
        updateData.stats = {
          ...stats,
          totalBorrows: (stats.totalBorrows || 0) + 1
        };
        break;
      case ACTIVITY_TYPES.COMPLETE_REPAIR:
        updateData.stats = {
          ...stats,
          totalRepairs: (stats.totalRepairs || 0) + 1
        };
        break;
    }

    await updateDoc(userRef, updateData);

    // Add T Coins if provided
    if (reward.coins > 0 && addTCoins) {
      try {
        await addTCoins(userId, reward.coinType, reward.coins, `รางวัลจาก: ${activityType}`);
      } catch (coinError) {
        console.error('Error adding T Coins:', coinError);
        // Continue even if coin addition fails
      }
    }

    // Create activity record
    const activityId = `${userId}_${Date.now()}_${activityType}`;
    const activityRef = doc(db, 'userActivities', activityId);
    await setDoc(activityRef, {
      activityId: activityId,
      userId,
      teamId: metadata.teamId || null,
      activityType,
      points: reward.points,
      xp: reward.xp,
      coins: reward.coins,
      coinType: reward.coinType,
      badgeUnlocked: null,
      achievementUnlocked: null,
      metadata,
      timestamp: serverTimestamp()
    });

    // Check for level up
    const { getMascotByXP } = await import('../data/nurseAppModels');
    const oldMascot = getMascotByXP(currentXP);
    const newMascot = getMascotByXP(newXP);

    let levelUpReward = null;
    if (newMascot && oldMascot && newMascot.level > oldMascot.level) {
      // Level up detected!
      levelUpReward = await awardLevelUpReward(userId, newMascot.level, addTCoins);
    }

    // Check for badge and achievement unlocks
    let unlockedBadges = [];
    let unlockedAchievements = [];
    try {
      const { checkAndUnlockBadges } = await import('./badgeManager');
      const { checkAndUnlockAchievements } = await import('./achievementManager');
      
      // Get updated user data
      const updatedUserSnap = await getDoc(userRef);
      if (updatedUserSnap.exists()) {
        const updatedUserData = updatedUserSnap.data();
        unlockedBadges = await checkAndUnlockBadges(userId, updatedUserData, addTCoins);
        unlockedAchievements = await checkAndUnlockAchievements(userId, updatedUserData, addTCoins);
      }
    } catch (error) {
      console.error('Error checking badges/achievements:', error);
    }

    return {
      success: true,
      rewards: {
        points: reward.points,
        xp: reward.xp,
        coins: reward.coins,
        coinType: reward.coinType
      },
      levelUp: levelUpReward,
      unlockedBadges,
      unlockedAchievements
    };
  } catch (error) {
    console.error('Error awarding rewards:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Award level up rewards
 * @param {string} userId - User ID
 * @param {number} newLevel - New level reached
 * @param {function} addTCoins - Function to add T Coins
 * @returns {Promise<object>} - Level up reward info
 */
const awardLevelUpReward = async (userId, newLevel, addTCoins) => {
  try {
    const levelRewards = {
      2: 100,
      3: 200,
      4: 300,
      5: 500
    };

    const coins = levelRewards[newLevel] || 0;
    if (coins > 0 && addTCoins) {
      await addTCoins(userId, TCOINS_TYPES.REAL, coins, `Level Up Reward: Level ${newLevel}`);
    }

    // Update user document
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      level: newLevel,
      lastLevelUpAt: serverTimestamp(),
      levelUpNotificationShown: false
    });

    // Award level up activity
    await awardRewards(userId, ACTIVITY_TYPES.LEVEL_UP, { level: newLevel }, addTCoins);

    return {
      level: newLevel,
      coins,
      coinType: TCOINS_TYPES.REAL
    };
  } catch (error) {
    console.error('Error awarding level up reward:', error);
    return null;
  }
};

/**
 * Get user gamification stats
 * @param {string} userId - User ID
 * @returns {Promise<object>} - User stats
 */
export const getUserGamificationStats = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      return null;
    }

    const userData = userSnap.data();
    const { getMascotByXP } = await import('../data/nurseAppModels');
    const mascot = getMascotByXP(userData.currentXP || 0);

    return {
      totalPoints: userData.totalPoints || 0,
      currentXP: userData.currentXP || 0,
      totalXP: userData.totalXP || 0,
      level: mascot?.level || 1,
      mascot: mascot,
      stats: userData.stats || {},
      badges: userData.badges || [],
      achievements: userData.achievements || []
    };
  } catch (error) {
    console.error('Error getting user gamification stats:', error);
    return null;
  }
};

